import type { RbacV2RolePermission } from '@gozoki/api-types'
import { createAppAbility, type PermissionTuple } from '~/utils/ability'
import { resolveMissingPermissions } from '~/utils/permissionRequirement'
import { PermissionRouteError } from '~/utils/rbac'
import { PRODUCT_MANAGEMENT_READ } from './product-management-rights'

/**
 * Accès à l'onglet jugé sur les grants v2 RÉELS de l'admin, quel que soit `permission-v2` (NH-40).
 *
 * Flag OFF, l'ability laisse passer tous les tuples v2 : les deux rôles de l'onglet, sans bit
 * hérité, n'y discriminent rien. Le service `apps/product-management`, lui, lit les vrais grants
 * (`GET /rbac/me/permissions`) et refuse un admin sans l'un des deux rôles : on évalue donc
 * `PRODUCT_MANAGEMENT_READ` sur une ability construite comme en v2, à partir de ces seuls grants.
 * Flag ON, c'est exactement l'ability installée : rien ne change. La route et l'entrée de
 * navigation de l'onglet s'en servent toutes les deux.
 */
const realGrantsAbility = (grantsV2: readonly RbacV2RolePermission[]) =>
  createAppAbility([], [...grantsV2], { permissionV2Enabled: true })

/** Rôles manquants pour l'onglet (les deux quand aucun n'est détenu), vide si l'accès est ouvert. */
export const missingProductManagementRoles = (
  grantsV2: readonly RbacV2RolePermission[],
): PermissionTuple[] =>
  resolveMissingPermissions(realGrantsAbility(grantsV2), PRODUCT_MANAGEMENT_READ)

export const hasProductManagementRole = (grantsV2: readonly RbacV2RolePermission[]): boolean =>
  missingProductManagementRoles(grantsV2).length === 0

/** `beforeLoad` de la route : page d'accès refusé (via `withRoutePermissions`) sans l'un des rôles. */
export const productManagementRouteGuard = ({
  context,
}: {
  context: { grantsV2: readonly RbacV2RolePermission[] }
}) => {
  const missing = missingProductManagementRoles(context.grantsV2)
  if (missing.length > 0) throw new PermissionRouteError(missing)
}
